import { useState, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Calendar as CalendarIcon, Clock, Video, User, ChevronsUpDown, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { isBefore, startOfDay, format, parse } from "date-fns";
import { cn } from "@/lib/utils";
import { toUTCDate, toUTCTime } from "@/lib/timeUtils";

const visitTypes = [
  { value: "general", label: "General Consultation" },
  { value: "followup", label: "Follow-up" },
  { value: "urgent", label: "Urgent Care" },
  { value: "mental_health", label: "Mental Health" },
];

const durations = [
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "45", label: "45 minutes" },
  { value: "60", label: "60 minutes" },
];

const timeSlots = Array.from({ length: 20 }, (_, i) => {
  const hour = Math.floor(i / 2) + 8;
  const min = i % 2 === 0 ? "00" : "30";
  const ampm = hour >= 12 ? "PM" : "AM";
  const displayHour = hour > 12 ? hour - 12 : hour;
  const value = `${String(hour).padStart(2, "0")}:${min}`;
  return {
    value,
    label: `${displayHour}:${min} ${ampm}`,
    // Check if this time slot is in the past for today
    isPast: (selectedDate: Date | undefined) => {
      if (!selectedDate) return false;
      const now = new Date();
      const isToday = startOfDay(selectedDate).getTime() === startOfDay(now).getTime();
      if (!isToday) return false;
      const slotTime = new Date(selectedDate);
      slotTime.setHours(hour, parseInt(min), 0, 0);
      return isBefore(slotTime, now);
    },
  };
});

interface NewSessionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NewSessionDialog({ open, onOpenChange }: NewSessionDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [date, setDate] = useState<Date | undefined>(undefined);
  const [time, setTime] = useState("");
  const [visitType, setVisitType] = useState("general");
  const [duration, setDuration] = useState("30");
  const [memberId, setMemberId] = useState("");
  const [visitReason, setVisitReason] = useState("");
  const [patientPopoverOpen, setPatientPopoverOpen] = useState(false);

  // Fetch only users with the 'member' role (patients) via single join query
  const { data: members = [] } = useQuery({
    queryKey: ["provider-members", user?.id],
    queryFn: async () => {
      // First get member user_ids, then fetch their profiles
      const { data: roles, error: rolesErr } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "member");
      if (rolesErr) throw rolesErr;
      const memberIds = (roles || []).map((r) => r.user_id);
      if (memberIds.length === 0) return [];
      const { data, error } = await supabase
        .from("profiles")
        .select("id, display_name, email")
        .in("id", memberIds);
      if (error) throw error;
      return data || [];
    },
    enabled: open && !!user,
  });

  const createSession = useMutation({
    mutationFn: async () => {
      if (!user || !date || !time || !memberId) throw new Error("Missing required fields");

      // Validate that the selected date/time is not in the past
      const selectedDateTime = parse(time, "HH:mm", new Date(date));
      if (isBefore(selectedDateTime, new Date())) {
        throw new Error("Cannot schedule sessions in the past. Please select a future date and time.");
      }

      // Convert local date+time to UTC for storage
      const utcDate = toUTCDate(date);
      const utcTime = toUTCTime(time, date);

      const { data: sessionData, error } = await supabase.from("telehealth_sessions").insert({
        provider_id: user.id,
        member_id: memberId,
        scheduled_date: utcDate,
        scheduled_time: utcTime,
        visit_type: visitType,
        visit_reason: visitReason || null,
        duration_minutes: parseInt(duration),
        status: "scheduled",
      }).select("id").single();
      if (error) throw error;

      // Auto-create Zoom meeting
      try {
        const { data: zoomData, error: zoomError } = await supabase.functions.invoke(
          "create-zoom-meeting",
          { body: { session_id: sessionData.id } }
        );
        if (zoomError) {
          console.error("Zoom meeting creation failed:", zoomError);
          // Check if it's a "no host available" error
          const errMsg = typeof zoomError === "string" ? zoomError : zoomError?.message || "";
          if (errMsg.includes("No available Zoom host") || (zoomData && zoomData.error?.includes("No available Zoom host"))) {
            Sentry.captureException(new Error("Zoom account not available"), {
              extra: {
                session_id: sessionData.id,
                provider_id: user.id,
                member_id: memberId,
                scheduled_date: utcDate,
                scheduled_time: utcTime,
              },
            });
            toast({ title: "Zoom Unavailable", description: "No Zoom account is available for this time. The session was created but without a Zoom meeting.", variant: "destructive" });
          }
        } else if (zoomData?.error) {
          // Edge function returned 200 but with error in body (e.g. 503)
          if (zoomData.error.includes("No available Zoom host")) {
            Sentry.captureException(new Error("Zoom account not available"), {
              extra: {
                session_id: sessionData.id,
                provider_id: user.id,
                member_id: memberId,
                scheduled_date: utcDate,
                scheduled_time: utcTime,
              },
            });
            toast({ title: "Zoom Unavailable", description: "No Zoom account is available for this time. The session was created but without a Zoom meeting.", variant: "destructive" });
          }
        }
      } catch (zoomErr) {
        console.error("Zoom meeting creation error:", zoomErr);
      }
    },
    onSuccess: () => {
      toast({ title: "Session scheduled", description: "The telehealth session has been created with a Zoom meeting." });
      queryClient.invalidateQueries({ queryKey: ["provider-sessions"] });
      queryClient.invalidateQueries({ queryKey: ["telehealth-schedule"] });
      queryClient.invalidateQueries({ queryKey: ["telehealth-schedule-all"] });
      resetForm();
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const resetForm = () => {
    setDate(undefined);
    setTime("");
    setVisitType("general");
    setDuration("30");
    setMemberId("");
    setVisitReason("");
  };

  // Validate form: date, time, and member must be selected, and time must not be in the past
  const isValid = () => {
    if (!date || !time || !memberId) return false;
    
    // Check if the selected date/time is in the past
    const selectedDateTime = parse(time, "HH:mm", new Date(date));
    return !isBefore(selectedDateTime, new Date());
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="h-5 w-5 text-primary" />
            Schedule New Session
          </DialogTitle>
          <DialogDescription>
            Create a new telehealth session for a patient.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Patient Selection with Search */}
          <div className="space-y-2">
            <Label>Patient</Label>
            <Popover open={patientPopoverOpen} onOpenChange={setPatientPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={patientPopoverOpen}
                  className="w-full justify-between font-normal"
                >
                  {memberId
                    ? members.find((m) => m.id === memberId)?.display_name ||
                      members.find((m) => m.id === memberId)?.email ||
                      "Unknown"
                    : "Search or select a patient..."}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search patients..." />
                  <CommandList>
                    <CommandEmpty>No patient found.</CommandEmpty>
                    <CommandGroup>
                      {members.map((m) => (
                        <CommandItem
                          key={m.id}
                          value={m.display_name || m.email || m.id}
                          onSelect={() => {
                            setMemberId(m.id);
                            setPatientPopoverOpen(false);
                          }}
                        >
                          <Check
                            className={cn(
                              "mr-2 h-4 w-4",
                              memberId === m.id ? "opacity-100" : "opacity-0"
                            )}
                          />
                          <User className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                          {m.display_name || m.email || "Unknown"}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Date Picker */}
          <div className="space-y-2">
            <Label>Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn("w-full justify-start text-left font-normal", !date && "text-muted-foreground")}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, "MMMM d, yyyy") : "Pick a date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => isBefore(d, startOfDay(new Date()))}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time */}
          <div className="space-y-2">
            <Label>Time</Label>
            <Select value={time} onValueChange={setTime}>
              <SelectTrigger>
                <SelectValue placeholder="Select time" />
              </SelectTrigger>
              <SelectContent>
                {timeSlots.map((t) => {
                  const isPast = t.isPast(date);
                  return (
                    <SelectItem key={t.value} value={t.value} disabled={isPast}>
                      <span className={cn("flex items-center gap-2", isPast && "text-muted-foreground opacity-50")}>
                        <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                        {t.label} {isPast && "(Past)"}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Visit Type & Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Visit Type</Label>
              <Select value={visitType} onValueChange={setVisitType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {visitTypes.map((v) => (
                    <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Duration</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {durations.map((d) => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Visit Reason */}
          <div className="space-y-2">
            <Label>Visit Reason (optional)</Label>
            <Textarea
              placeholder="Brief reason for the visit..."
              value={visitReason}
              onChange={(e) => setVisitReason(e.target.value)}
              rows={2}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              variant="gradient"
              disabled={!isValid() || createSession.isPending}
              onClick={() => createSession.mutate()}
            >
              {createSession.isPending ? "Scheduling..." : "Schedule Session"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
